import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import MenuIcon from '@mui/icons-material/Menu'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AppsOutlined, BookTwoTone, DashboardOutlined, DomainAddOutlined, GroupsOutlined, HandymanRounded, Person4Sharp, PoolTwoTone, HomeWorkOutlined } from '@mui/icons-material'
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined'
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined'
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined'
import { useAuth } from '../contexts/AuthContext'
import { useCondominium } from '../contexts/CondominioContext'
import { useTenantModulos } from '../hooks/useTenantModulos'
import { getContexto } from '../utils/contexto'
import type { Permissao, SubscricaoModulo } from '../utils/types'
import { NAVBAR_HEIGHT } from './Navbar'
import indlusFavicon from '../../assets/indlusfavico.png'

export const SIDEBAR_WIDTH = 260

interface SubItem {
    id: string
    label: string
    path: string
    icon: React.ReactNode
}

interface NavItem {
    id: string
    label: string
    icon: React.ReactNode
    path?: string
    children?: SubItem[]
}

// Permissão(ões) necessárias para ver cada item do menu — basta ter UMA das
// listadas (equivalente ao `@PreAuthorize` do endpoint GET/list que a página
// consome no backend). Um item sem entrada aqui é sempre visível (não há
// nenhum, hoje todos os módulos mapeiam para pelo menos uma permissão).
//
// Os itens "resumo"/"overview" de um grupo (ex: access-control-overview) usam
// a união das permissões dos irmãos, para aparecerem sempre que pelo menos um
// sub-módulo do grupo estiver visível.
const ITEM_PERMISSIONS: Record<string, Permissao[]> = {
    // ESTATE_* (Estates/Propriedades) é a gestão de base da mesma propriedade —
    // tratado como equivalente a CONDOMINIO_* nas páginas de base.
    dashboard: ['CONDOMINIO_READ', 'ESTATE_READ'],
    buildings: ['CONDOMINIO_READ', 'ESTATE_READ'],
    blocks: ['CONDOMINIO_READ', 'ESTATE_READ'],
    units: ['CONDOMINIO_READ', 'ESTATE_READ', 'UNIDADE_READ', 'UNIDADE_GERIR_PROPRIA', 'UNIDADE_ASSOCIAR_ENTIDADE_PROPRIA'],
    residents: ['ENTIDADE_READ'],

    'finance-accounts': ['FINANCAS_READ'],
    'finance-incomes': ['FINANCAS_READ'],
    'finance-charges': ['COBRANCA_READ'],
    'finance-payments': ['FINANCAS_READ'],
    'finance-expenses': ['FINANCAS_READ'],
    'finance-expense-payments': ['FINANCAS_READ'],
    // Transferências/Fornecedores não usam FINANCAS_READ sozinho — ao
    // contrário dos restantes itens de finanças, um papel só com leitura
    // genérica (sem nenhuma permissão própria destas duas áreas) não tem
    // nada para fazer aqui, por isso o item fica fora do menu (mesmo que o
    // backend ainda deixe listar por FINANCAS_READ — ver TransferenciaController/
    // FornecedorController).
    'finance-transfers': ['FINANCAS_MANAGE', 'FINANCAS_CRIAR', 'TRANSFERENCIA_EXECUTAR'],
    'finance-suppliers': ['FINANCAS_MANAGE', 'FINANCAS_CRIAR', 'FORNECEDOR_READ', 'FORNECEDOR_CRIAR', 'FORNECEDOR_EDITAR', 'FORNECEDOR_ELIMINAR', 'FORNECEDOR_MANAGE'],

    'access-control-visits-scheduling': ['VISITANTE_READ'],
    'access-control-visits': ['VISITANTE_READ'],
    'access-control-visitors': ['VISITANTE_READ'],
    'access-control-vehicles': ['VEICULO_READ'],
    // Histórico (entradas/saídas de TODOS os veículos) não usa VEICULO_READ —
    // esse cobre só a ficha do próprio veículo (entidade tem-no via
    // VEICULO_REGISTAR_PROPRIO, mas não deve ver o histórico do condomínio).
    'access-control-vehicles-log': ['VEICULO_MANAGE', 'VEICULO_HISTORICO_READ'],
    'access-control-packages': ['ENCOMENDA_READ'],

    'common-areas-overview': ['ESPACO_COMUM_READ'],
    // A listagem de reservas (mesmo "as minhas") exige RESERVA_READ no
    // backend — só criar uma reserva aceita RESERVA_CRIAR isoladamente.
    'common-areas-my-reservations': ['RESERVA_READ'],
    'common-areas-approvals': ['RESERVA_MANAGE'],
    'common-areas-calendar': ['RESERVA_READ'],
    'common-areas-history': ['RESERVA_READ'],

    'parking-overview': ['PARQUE_ESTACIONAMENTO_READ'],
    'parking-slots': ['VAGA_ESTACIONAMENTO_READ'],
    'parking-assignments': ['ATRIBUICAO_ESTACIONAMENTO_READ'],
    'parking-authorizations': ['AUTORIZACAO_ESTACIONAMENTO_READ', 'AUTORIZACAO_ESTACIONAMENTO_CRIAR_PROPRIA'],

    'maintenance-assets': ['MANUTENCAO_READ'],
    'maintenance-requests': ['MANUTENCAO_READ'],
    'maintenance-service-orders': ['MANUTENCAO_READ'],
    // Planeamento preventivo é exclusivo de quem gere manutenção — ao
    // contrário de activos/solicitações, não é visível a condóminos.
    'maintenance-schedules': ['MANUTENCAO_MANAGE'],
    'arrendamentos-meus': ['ARRENDAMENTO_GERIR_PROPRIO'],
    'arrendamentos-cobrancas': ['ARRENDAMENTO_GERIR_PROPRIO'],
    'arrendamentos-meu-arrendamento': ['ARRENDAMENTO_GERIR_PROPRIO', 'ARRENDAMENTO_CONSULTAR_PROPRIO'],
    'arrendamentos-faturas': ['ARRENDAMENTO_GERIR_PROPRIO', 'ARRENDAMENTO_CONSULTAR_PROPRIO'],

    'communication-comunicados': ['COMUNICADO_READ'],
    'communication-templates': ['COMUNICADO_READ'],
    documents: ['COMUNICADO_READ'],

    assembleias: ['ASSEMBLEIA_READ', 'VOTACAO_READ'],
    'assembleias-list': ['ASSEMBLEIA_READ'],
    'assembleias-votacoes': ['VOTACAO_READ'],

    users: ['USERS_READ'],
}

function canSee(hasPermission: (p: Permissao) => boolean, itemId: string): boolean {
    const required = ITEM_PERMISSIONS[itemId]
    if (!required || required.length === 0) return true
    return required.some((p) => hasPermission(p))
}

// Itens que só fazem sentido em contexto de condomínio — escondidos apenas
// quando o subdomínio actual for explicitamente "prop"; em 'NEUTRO'
// (localhost, ou o domínio único ainda por migrar para subdomínios) mantêm-se
// visíveis, para não regredir o comportamento actual antes da migração estar
// concluída.
const CONDOMINIUM_ONLY_ITEMS = new Set(['assembleias'])

function canSeeInContext(itemId: string): boolean {
    if (!CONDOMINIUM_ONLY_ITEMS.has(itemId)) return true
    return getContexto() !== 'PROPRIEDADE'
}

// Módulos 100% self-service — todas as sub-rotas exigem uma permissão *_PROPRIO
// e são resolvidas pelo servidor a partir da entidade do próprio utilizador no
// condomínio activo. Sem entidade associada não há nada "próprio" para mostrar,
// por isso o grupo inteiro desaparece (mesmo que o papel conceda a permissão).
const MEMBER_ONLY_ITEMS = new Set(['arrendamentos'])

function canSeeAsMember(itemId: string, temEntidade: boolean): boolean {
    if (!MEMBER_ONLY_ITEMS.has(itemId)) return true
    return temEntidade
}

// Módulo da subscrição a que cada grupo de topo pertence — o grupo inteiro (e
// as suas rotas) desaparece quando o plano do tenant não o inclui. Itens de
// gestão de base (dashboard, condominio, users) não têm módulo → sempre visíveis.
const ITEM_MODULO: Record<string, SubscricaoModulo> = {
    finance: 'FINANCAS',
    'access-control': 'CONTROLO_ACESSO',
    'common-areas': 'AREAS_COMUNS',
    parking: 'ESTACIONAMENTO',
    maintenance: 'MANUTENCAO',
    arrendamentos: 'ARRENDAMENTO',
    communication: 'COMUNICACAO',
    assembleias: 'ASSEMBLEIAS',
}

interface Props {
    mobileOpen: boolean
    onClose: () => void
    collapsed?: boolean
    onToggleCollapse?: () => void
}

export default function Sidebar({ mobileOpen, onClose, collapsed = false, onToggleCollapse }: Props) {
    const { t } = useTranslation('nav')
    const { hasPermission, user } = useAuth()
    const { temModulo } = useTenantModulos()
    const { activeCondominium } = useCondominium()
    const location = useLocation()
    const { id } = useParams<{ id: string }>()
    const [openMenu, setOpenMenu] = useState<string | null>(null)

    // Base path: se estivermos dentro de um condomínio activo, as rotas usam /properties/:id/...
    const base = id ? `/properties/${id}` : '/properties'

    const navItems: NavItem[] = [
        {
            id: 'dashboard',
            label: t('sidebar.dashboard'),
            icon: <DashboardOutlined fontSize="small" />,
            path: `${base}`,
        },
        {
            id: 'condominio',
            label: t('sidebar.condominium'),
            icon: <ApartmentOutlinedIcon fontSize="small" />,
            children: [
                ...(activeCondominium?.temPredios ? [{ id: 'buildings', label: t('sidebar.buildings'), path: `${base}/buildings`, icon: <DomainAddOutlined fontSize="small" /> }] : []),
                ...(activeCondominium?.temBlocos ? [{ id: 'blocks', label: t('sidebar.blocks'), path: `${base}/blocks`, icon: <AppsOutlined fontSize="small" /> }] : []),
                { id: 'units', label: t('sidebar.units'), path: `${base}/units`, icon: <MeetingRoomOutlinedIcon fontSize="small" /> },
                { id: 'residents', label: t('sidebar.residents'), path: `${base}/residents`, icon: <Person4Sharp fontSize="small" /> },
            ],
        },
        {
            id: 'finance',
            label: t('sidebar.finance'),
            icon: <AccountBalanceWalletOutlinedIcon fontSize="small" />,
            path: `${base}/finance`,
            children: [
                { id: 'finance-accounts', label: t('sidebar.financeAccounts'), path: `${base}/finance/accounts`, icon: <></> },
                { id: 'finance-incomes', label: t('sidebar.financeIncomes'), path: `${base}/finance/incomes`, icon: <></> },
                { id: 'finance-charges', label: t('sidebar.financeCharges'), path: `${base}/finance/charges`, icon: <></> },
                { id: 'finance-payments', label: t('sidebar.financePayments'), path: `${base}/finance/payments`, icon: <></> },
                { id: 'finance-expenses', label: t('sidebar.financeExpenses'), path: `${base}/finance/expenses`, icon: <></> },
                { id: 'finance-expense-payments', label: t('sidebar.financeExpensePayments'), path: `${base}/finance/expense-payments`, icon: <></> },
                { id: 'finance-transfers', label: t('sidebar.financeTransfers'), path: `${base}/finance/transfers`, icon: <></> },
                { id: 'finance-suppliers', label: t('sidebar.financeSuppliers'), path: `${base}/finance/suppliers`, icon: <></> }
            ]
        },
        {
            id: 'access-control',
            label: t('sidebar.accessControl'),
            icon: <BadgeOutlinedIcon fontSize="small" />,
            path: `${base}/dashboard/acesso`,
            children: [
                { id: 'access-control-visits-scheduling', label: t('sidebar.accessControlVisitScheduling'), path: `${base}/access-control/visits`, icon: <EventAvailableOutlinedIcon fontSize="small" /> },
                { id: 'access-control-visits', label: t('sidebar.accessControlVisits'), path: `${base}/access-control/visits-log`, icon: <HowToRegOutlinedIcon fontSize="small" /> },
                { id: 'access-control-visitors', label: t('sidebar.accessControlVisitors'), path: `${base}/access-control/visitors`, icon: <PermIdentityOutlinedIcon fontSize="small" /> },
                { id: 'access-control-vehicles', label: t('sidebar.accessControlVehicles'), path: `${base}/access-control/vehicles`, icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
                { id: 'access-control-vehicles-log', label: t('sidebar.accessControlVehiclesLog'), path: `${base}/access-control/vehicles-log`, icon: <HowToRegOutlinedIcon fontSize="small" /> },
                { id: 'access-control-packages', label: t('sidebar.accessControlPackages'), path: `${base}/access-control/packages`, icon: <Inventory2OutlinedIcon fontSize="small" /> },
            ],
        },
        {
            id: 'common-areas',
            label: t('sidebar.commonAreas'),
            icon: <PoolTwoTone fontSize="small" />,
            path: `${base}/common-areas`,
            children: [
                { id: 'common-areas-overview', label: t('sidebar.commonAreasOverview'), path: `${base}/common-areas`, icon: <PoolTwoTone fontSize="small" /> },
                { id: 'common-areas-my-reservations', label: t('sidebar.commonAreasMyReservations'), path: `${base}/common-areas/my-reservations`, icon: <EventAvailableOutlinedIcon fontSize="small" /> },
                { id: 'common-areas-approvals', label: t('sidebar.commonAreasApprovals'), path: `${base}/common-areas/approvals`, icon: <CheckCircleOutlineOutlined fontSize="small" /> },
                { id: 'common-areas-calendar', label: t('sidebar.commonAreasCalendar'), path: `${base}/common-areas/calendar`, icon: <CalendarMonthOutlined fontSize="small" /> },
                { id: 'common-areas-history', label: t('sidebar.commonAreasHistory'), path: `${base}/common-areas/history`, icon: <HowToRegOutlinedIcon fontSize="small" /> },
            ],
        },
        {
            id: 'parking',
            label: t('sidebar.parking'),
            icon: <LocalParkingOutlinedIcon fontSize="small" />,
            path: `${base}/parking`,
            children: [
                { id: 'parking-overview', label: t('sidebar.parkingAreas'), path: `${base}/parking`, icon: <LocalParkingOutlinedIcon fontSize="small" /> },
                { id: 'parking-slots', label: t('sidebar.parkingSlots'), path: `${base}/parking/slots`, icon: <MeetingRoomOutlinedIcon fontSize="small" /> },
                { id: 'parking-assignments', label: t('sidebar.parkingAssignments'), path: `${base}/parking/assignments`, icon: <AssignmentIndOutlinedIcon fontSize="small" /> },
                { id: 'parking-authorizations', label: t('sidebar.parkingAuthorizations'), path: `${base}/parking/authorizations`, icon: <VerifiedUserOutlinedIcon fontSize="small" /> },
            ],
        },
        {
            id: 'maintenance',
            label: t('sidebar.maintenance'),
            icon: <HandymanRounded fontSize="small" />,
            path: `${base}/dashboard/manutencao`,
            children: [
                { id: 'maintenance-assets', label: t('sidebar.maintenanceAssets'), path: `${base}/maintenance/assets`, icon: <></> },
                { id: 'maintenance-requests', label: t('sidebar.maintenanceRequests'), path: `${base}/maintenance/requests`, icon: <></> },
                { id: 'maintenance-service-orders', label: t('sidebar.maintenanceServiceOrders'), path: `${base}/maintenance/service-orders`, icon: <></> },
                { id: 'maintenance-schedules', label: t('sidebar.maintenanceSchedules'), path: `${base}/maintenance/schedules`, icon: <></> },
            ],
        },
        {
            id: 'arrendamentos',
            label: t('rentals:title'),
            icon: <HomeWorkOutlined fontSize="small" />,
            children: [
                { id: 'arrendamentos-meus', label: t('rentals:menu.meusArrendamentos'), path: `${base}/arrendamentos/meus`, icon: <></> },
                { id: 'arrendamentos-cobrancas', label: t('rentals:menu.cobrancasRenda'), path: `${base}/arrendamentos/cobrancas`, icon: <></> },
                { id: 'arrendamentos-meu-arrendamento', label: t('rentals:menu.meuArrendamento'), path: `${base}/arrendamentos/meu-arrendamento`, icon: <></> },
                { id: 'arrendamentos-faturas', label: t('rentals:menu.minhasFaturas'), path: `${base}/arrendamentos/faturas`, icon: <></> },
            ],
        },
        {
            id: 'communication',
            label: t('sidebar.communication'),
            icon: <CampaignOutlinedIcon fontSize="small" />,
            path: `${base}/communication`,
            children: [
                { id: 'communication-comunicados', label: t('sidebar.communicationComunicados'), path: `${base}/communication/comunicados`, icon: <></> },
                { id: 'communication-templates', label: t('sidebar.communicationTemplates'), path: `${base}/communication/templates`, icon: <></> },
                { id: 'documents', label: t('sidebar.documents'), path: `${base}/documents`, icon: <BookTwoTone fontSize="small" /> },
            ],
        },
        {
            id: 'assembleias',
            label: t('sidebar.assembleias'),
            icon: <GroupsOutlined fontSize="small" />,
            children: [
                { id: 'assembleias-list', label: t('sidebar.assembleias'), path: `${base}/assembleias`, icon: <GroupsOutlined fontSize="small" /> },
                { id: 'assembleias-votacoes', label: t('sidebar.assembleiasVotacoes'), path: `${base}/assembleias/votacoes`, icon: <HowToVoteOutlinedIcon fontSize="small" /> },
            ],
        },
        {
            id: 'users',
            label: t('sidebar.users'),
            icon: <PeopleOutlineOutlined fontSize="small" />,
            path: `${base}/users`,
        },
    ]

    // Esconde módulos inteiros a que o utilizador não tenha nenhuma permissão
    // de acesso — um grupo com sub-itens só fica visível se sobrar pelo menos
    // um filho visível; um item sem sub-itens usa a sua própria entrada em
    // ITEM_PERMISSIONS.
    const moduloOk = (itemId: string) => {
        const m = ITEM_MODULO[itemId]
        return !m || temModulo(m)
    }

    const visibleNavItems = navItems
        .filter((item) => moduloOk(item.id) && canSeeAsMember(item.id, !!user?.temEntidade))
        .map((item) => (
            item.children
                ? { ...item, children: item.children.filter((child) => canSee(hasPermission, child.id)) }
                : item
        ))
        .filter((item) => (item.children
            ? item.children.length > 0 && canSeeInContext(item.id)
            : canSee(hasPermission, item.id) && canSeeInContext(item.id)))

    // Expande automaticamente o submenu correspondente à rota actual — usa o
    // filho com o caminho mais específico (mais longo) que corresponda, não
    // só o primeiro encontrado. Necessário porque, p.ex., o "Resumo" das
    // Finanças aponta para a raiz do módulo (`${base}/finance`), que é
    // prefixo de todas as outras rotas do grupo; com `startsWith` simples,
    // esse item "ganhava" sempre e o menu Finanças ficava permanentemente
    // aberto ao navegar para qualquer sub-página do módulo.
    useEffect(() => {
        let bestParentId: string | null = null
        let bestLength = -1
        visibleNavItems.forEach((item) => {
            item.children?.forEach((child) => {
                const matches = location.pathname === child.path || location.pathname.startsWith(`${child.path}/`)
                if (matches && child.path.length > bestLength) {
                    bestParentId = item.id
                    bestLength = child.path.length
                }
            })
        })
        if (bestParentId) setOpenMenu(bestParentId)
    }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

    // Comparação exacta (não por prefixo): a rota "Resumo" das Finanças usa o
    // mesmo caminho do item pai (`${base}/finance`), que é prefixo de todos os
    // seus irmãos (`${base}/finance/accounts`, etc.) — usar `startsWith` fazia
    // o "Resumo" aparecer sempre seleccionado em qualquer sub-página de Finanças.
    //
    // Excepção: o Painel (`${base}`) tem secções internas em `${base}/dashboard/...`
    // (Financeiro, Acesso, Manutenção) que devem manter o item "Painel" selecionado.
    const isActive = (path: string) =>
        location.pathname === path ||
        (path === base && location.pathname.startsWith(`${base}/dashboard/`))

    const handleToggle = (id: string) => {
        setOpenMenu((prev) => (prev === id ? null : id))
    }

    const content = (
        <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Cabeçalho do drawer móvel — só aqui para dar uma indicação
                explícita de como fechar o menu, além do toque fora dele. */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #E5E7EB',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('sidebar.menu')}
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label={t('sidebar.closeMenu')}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: 2.5, py: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                    {t('sidebar.management')}
                </Typography>
            </Box>

            <List sx={{ px: 1.5, flexGrow: 1 }}>
                {visibleNavItems.map((item) => {
                    if (item.children) {
                        const open = openMenu === item.id
                        const hasActiveChild = item.children.some((child) => isActive(child.path))

                        return (
                            <Box key={item.id} sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleToggle(item.id)}
                                    sx={{
                                        borderRadius: 1,
                                        color: hasActiveChild ? 'primary.main' : 'text.primary',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        
                                    />
                                    {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </ListItemButton>

                                <Collapse in={open} timeout="auto" unmountOnExit>
                                    <List disablePadding sx={{ pl: 1 }}>
                                        {item.children.map((child) => {
                                            const active = isActive(child.path)
                                            return (
                                                <ListItemButton
                                                    key={child.path}
                                                    component={RouterLink}
                                                    to={child.path}
                                                    onClick={onClose}
                                                    selected={active}
                                                    sx={{
                                                        borderRadius: 1,
                                                        ml: 1.5,
                                                        mb: 0.25,
                                                        color: active ? 'primary.main' : 'text.secondary',
                                                        '&.Mui-selected': {
                                                            bgcolor: '#EEF2FF',
                                                            '&:hover': { bgcolor: '#E0E7FF' },
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                                                        {child.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={child.label}
                                                    />
                                                </ListItemButton>
                                            )
                                        })}
                                    </List>
                                </Collapse>
                            </Box>
                        )
                    }

                    const active = isActive(item.path!)
                    return (
                        <ListItemButton
                            key={item.id}
                            component={RouterLink}
                            to={item.path!}
                            onClick={onClose}
                            selected={active}
                            sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                color: active ? 'primary.main' : 'text.primary',
                                '&.Mui-selected': {
                                    bgcolor: '#EEF2FF',
                                    '&:hover': { bgcolor: '#E0E7FF' },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText
                                primary={item.label}
                            />
                        </ListItemButton>
                    )
                })}
            </List>

            <Divider />
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                    component="img"
                    src={indlusFavicon}
                    alt=""
                    sx={{ height: 16, width: 'auto', display: 'block', flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                    {t('sidebar.footer', { year: new Date().getFullYear() })}
                </Typography>
            </Box>
        </Box>
    )

    return (
        <>
            {/* Mobile: drawer temporário, fecha ao navegar ou tocar fora */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: SIDEBAR_WIDTH,
                        boxSizing: 'border-box',
                        // Sem isto, o painel arranca em y:0 e fica escondido atrás
                        // da Navbar fixa (zIndex superior ao do Drawer) — incluindo
                        // o cabeçalho "Menu"/fechar, que ficava invisível.
                        top: NAVBAR_HEIGHT,
                        height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
                    },
                }}
            >
                {content}
            </Drawer>

            {/* Desktop: botão fixo no topo da sidebar (fora da área com scroll),
                por isso não se move quando a lista é percorrida. Fica encostado
                à borda direita da sidebar quando aberta, ou à borda esquerda do
                ecrã quando escondida. */}
            {onToggleCollapse && (
                <Tooltip title={t(collapsed ? 'sidebar.showSidebar' : 'sidebar.hideSidebar')} placement="right">
                    <IconButton
                        onClick={onToggleCollapse}
                        aria-label={t(collapsed ? 'sidebar.showSidebar' : 'sidebar.hideSidebar')}
                        size="small"
                        sx={{
                            display: { xs: 'none', md: 'inline-flex' },
                            position: 'fixed',
                            top: NAVBAR_HEIGHT + 8,
                            left: collapsed ? 8 : SIDEBAR_WIDTH - 40,
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            bgcolor: 'background.paper',
                            border: '1px solid #E5E7EB',
                            boxShadow: 1,
                            transition: (theme) =>
                                theme.transitions.create('left', {
                                    easing: collapsed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                                    duration: collapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen,
                                }),
                            '&:hover': { bgcolor: '#F0F4FF' },
                        }}
                    >
                        {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            )}

            {/* Desktop: drawer persistente — pode ser escondido pelo botão fixo acima */}
            <Drawer
                variant="persistent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': {
                        width: SIDEBAR_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #E5E7EB',
                        top: NAVBAR_HEIGHT, // altura da Navbar (AppBar)
                        height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
                    },
                }}
                open={!collapsed}
            >
                {content}
            </Drawer>
        </>
    )
}
